import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorSideEffects, loadingSideEffects, RTKSideEffectSchema, successSideEffects } from "..";
import { Toastify } from "../../../components/Toaster";

export interface SigningKeyToken {
  Kid: string;
  VerifyKey: string;
  ExpireAt?: string;
  status?: "in-use" | "next" | "previous";
  Fingerprint?: string;
  Thumbprint?: string;
  ClientId?: string;
  Name?: string;
}

export interface SigningKeysState extends RTKSideEffectSchema {
  keys: SigningKeyToken[];
  revokedKeys: SigningKeyToken[];
  appSigningKeys: SigningKeyToken[];
  appActiveKeyId: string | null;
  isRotating: boolean;
  isRotateAndRevoke: boolean;
  isAppKeyCreating: boolean;
  isAppKeyRotating: boolean;
}

const initialState: SigningKeysState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: "",
  keys: [],
  revokedKeys: [],
  appSigningKeys: [],
  appActiveKeyId: null,
  isRotating: false,
  isRotateAndRevoke: false,
  isAppKeyCreating: false,
  isAppKeyRotating: false
};

const signingKeysSlice = createSlice({
  name: "signingKeys",
  initialState,
  reducers: {
    // Fetch signing keys
    getSigningKeysRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getSigningKeysSuccess(state, action: PayloadAction<any>) {
      Object.assign(state, successSideEffects);

      const response = action.payload;

      // Process tokens with status labels
      const activeTokens = (response.Tokens || []).map((t: any) => ({ ...t, status: "in-use" }));
      const nextTokens = (response.NextTokens || []).map((t: any) => ({ ...t, status: "next" }));
      const previousTokens = (response.PreviousTokens || []).map((t: any) => ({ ...t, status: "previous" }));
      const allKeys = [...activeTokens, ...nextTokens, ...previousTokens];

      // Separate per-app keys from platform keys
      const appSigningKeys = allKeys.filter((k: any) => k.ClientId);

      // Compute active app key ID
      const activeAppKey = appSigningKeys.find((k: any) => k.status === "in-use");
      const appActiveKeyId = activeAppKey?.Kid ?? (appSigningKeys.length > 0 ? appSigningKeys[0].Kid : null);

      // Process revoked tokens
      const normalizedRevoked = (response.RevokedTokens || []).map((t: any) => ({
        ...t,
        ExpireAt: t.RotateAt || t.StartAt || ""
      }));

      const sortedRevoked = [...normalizedRevoked]
        .sort((a, b) => {
          const timeA = a.ExpireAt ? new Date(a.ExpireAt).getTime() : 0;
          const timeB = b.ExpireAt ? new Date(b.ExpireAt).getTime() : 0;
          return timeB - timeA;
        })
        .slice(0, 3);

      state.keys = allKeys;
      state.revokedKeys = sortedRevoked;
      state.appSigningKeys = appSigningKeys;
      state.appActiveKeyId = appActiveKeyId;
    },
    getSigningKeysError(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      Toastify("error", action.payload);
    },

    // Rotate key
    rotateSigningKeyRequest(state) {
      state.isRotating = true;
    },
    rotateSigningKeySuccess(state) {
      state.isRotating = false;
      Toastify("success", "Signing key rotated successfully.");
    },
    rotateSigningKeyError(state, action: PayloadAction<string>) {
      state.isRotating = false;
      Toastify("error", action.payload);
    },

    // Rotate N Revoke key
    rotateNRevokeSigningKeyRequest(state) {
      state.isRotateAndRevoke = true;
    },
    rotateNRevokeSigningKeySuccess(state) {
      state.isRotateAndRevoke = false;
      Toastify("success", "Signing keys rotated and revoked successfully.");
    },
    rotateNRevokeSigningKeyError(state, action: PayloadAction<string>) {
      state.isRotateAndRevoke = false;
      Toastify("error", action.payload);
    },

    // Per-app signing key: create
    createAppSigningKeyRequest(state, _action: PayloadAction<{ clientName: string }>) {
      state.isAppKeyCreating = true;
    },
    createAppSigningKeySuccess(state) {
      state.isAppKeyCreating = false;
      Toastify("success", "App signing key created successfully.");
    },
    createAppSigningKeyError(state, action: PayloadAction<string>) {
      state.isAppKeyCreating = false;
      Toastify("error", action.payload);
    },

    // Per-app signing key: rotate
    rotateAppSigningKeyRequest(state, _action: PayloadAction<{ clientName: string }>) {
      state.isAppKeyRotating = true;
    },
    rotateAppSigningKeySuccess(state) {
      state.isAppKeyRotating = false;
      Toastify("success", "App signing key rotated successfully.");
    },
    rotateAppSigningKeyError(state, action: PayloadAction<string>) {
      state.isAppKeyRotating = false;
      Toastify("error", action.payload);
    }
  }
});

export const {
  getSigningKeysRequest,
  getSigningKeysSuccess,
  getSigningKeysError,
  rotateSigningKeyRequest,
  rotateSigningKeySuccess,
  rotateSigningKeyError,
  rotateNRevokeSigningKeyRequest,
  rotateNRevokeSigningKeySuccess,
  rotateNRevokeSigningKeyError,
  createAppSigningKeyRequest,
  createAppSigningKeySuccess,
  createAppSigningKeyError,
  rotateAppSigningKeyRequest,
  rotateAppSigningKeySuccess,
  rotateAppSigningKeyError
} = signingKeysSlice.actions;

export default signingKeysSlice.reducer;
